// Pure scenario arithmetic shared by anonymous UI and authoritative report capture.
// No network, storage, identity, jurisdiction rules or commercial plan inference.
export const CALCULATION_VERSION = '1.0.0';
export const TOOL_CURRENCIES = Object.freeze(['USD','CAD','EUR','GBP','AUD','NZD','JPY','CHF','INR','AED','SAR','TTD','JMD','BBD','XCD','BRL','MXN']);
function amount(value, name, optional = false) {
  if (optional && (value === undefined || value === null || value === '')) return 0;
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > 1e9) throw new RangeError(`Invalid ${name}`);
  return value;
}
function periods(weekly) { return { weekly, monthly: weekly * 52 / 12, annual: weekly * 52 }; }
export function calculateRoi(input) {
  const adminHours = amount(input.adminHours,'adminHours');
  const adminRate = amount(input.adminRate,'adminRate');
  const supervisorHours = amount(input.supervisorHours,'supervisorHours');
  const supervisorRate = input.supervisorRate == null ? adminRate : amount(input.supervisorRate,'supervisorRate');
  const approvalHours = amount(input.approvalHours,'approvalHours',true);
  const approvalRate = input.approvalRate == null ? adminRate : amount(input.approvalRate,'approvalRate');
  const correctionFrequency = amount(input.correctionFrequency,'correctionFrequency',true);
  const correctionCost = amount(input.correctionCost,'correctionCost',true);
  const reductionPercent = amount(input.reductionPercent,'reductionPercent');
  if (reductionPercent > 100) throw new RangeError('Invalid reductionPercent');
  const weekly = adminHours * adminRate + supervisorHours * supervisorRate + approvalHours * approvalRate + correctionFrequency * correctionCost;
  return { baseline: periods(weekly), potentialSavings: periods(weekly * reductionPercent / 100),
    assumptions: { weeksPerYear: 52, monthsPerYear: 12, reductionPercent,
      supervisorRateUsesAdminRate: input.supervisorRate == null, approvalRateUsesAdminRate: input.approvalRate == null },
    classification: 'scenario_estimate' };
}
export function calculateStaffing(input) {
  const headcount = amount(input.headcount,'headcount');
  if (!Number.isInteger(headcount)) throw new RangeError('Invalid headcount');
  const shifts = amount(input.shiftsPerWorkerPerWeek,'shiftsPerWorkerPerWeek');
  const duration = amount(input.shiftHours,'shiftHours');
  const compensation = amount(input.hourlyCompensation,'hourlyCompensation');
  const loadedRate = input.loadedHourlyCost == null ? compensation : amount(input.loadedHourlyCost,'loadedHourlyCost');
  const overtimeHours = amount(input.additionalOvertimeHoursPerWorker,'additionalOvertimeHoursPerWorker',true);
  const overtimeMultiplier = input.overtimeMultiplier == null ? 1 : amount(input.overtimeMultiplier,'overtimeMultiplier');
  if (overtimeMultiplier < 1 || overtimeMultiplier > 10) throw new RangeError('Invalid overtimeMultiplier');
  const temporaryHours = amount(input.temporaryHours,'temporaryHours',true);
  const temporaryRate = amount(input.temporaryHourlyCost,'temporaryHourlyCost',true);
  const unfilledHours = amount(input.unfilledHours,'unfilledHours',true);
  const exposureRate = amount(input.unfilledExposurePerHour,'unfilledExposurePerHour',true);
  const regularHours = headcount * shifts * duration;
  const regularCost = regularHours * loadedRate;
  const overtimeCost = headcount * overtimeHours * loadedRate * overtimeMultiplier;
  const temporaryCost = temporaryHours * temporaryRate;
  return { paidLaborCost: periods(regularCost + overtimeCost + temporaryCost), unfilledExposure: periods(unfilledHours * exposureRate),
    weeklyHours: regularHours + headcount * overtimeHours + temporaryHours,
    breakdown: { regularCost, overtimeCost, temporaryCost },
    assumptions: { weeksPerYear: 52, monthsPerYear: 12, loadedCostSupplied: input.loadedHourlyCost != null,
      overtimeMultiplier, overtimeHoursAreAdditional: true, exposureIsSeparateFromPaidCost: true }, classification: 'scenario_estimate' };
}
export function calculatePlanning(input) {
  const demand = amount(input.requiredHoursPerWeek,'requiredHoursPerWeek');
  const workers = amount(input.availableWorkers,'availableWorkers');
  if (!Number.isInteger(workers)) throw new RangeError('Invalid availableWorkers');
  const hours = amount(input.hoursPerWorkerPerWeek,'hoursPerWorkerPerWeek');
  const rate = amount(input.loadedHourlyCost,'loadedHourlyCost');
  const availableHours = workers * hours;
  return { availableHours, demandHours: demand, requiredWorkers: hours > 0 ? Math.ceil(demand / hours) : null,
    coveragePercent: demand > 0 ? Math.min(100, availableHours / demand * 100) : null,
    understaffedHours: Math.max(0,demand-availableHours), excessHours: Math.max(0,availableHours-demand),
    plannedCost: periods(availableHours*rate), demandCost: periods(demand*rate),
    assumptions: { weeksPerYear:52, monthsPerYear:12, availabilityAndDemandAreUserSupplied:true }, classification:'scenario_estimate' };
}
